import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import ClientSearchPicker from "../common/ClientSearchPicker";
import { scheduleUrl } from "../../../utils/adminNav";
import { toDateInputValue } from "../../../utils/appointmentLabels";
import L from "../../../constants/baysportLabels";
import { trackActivationComplete } from "../../../analytics/metrikaGoals";
import GroupSchedulePanel from "./GroupSchedulePanel";
import GroupLeaderboardPanel from "../common/GroupLeaderboardPanel";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import aa from "../AdminPortal/adminApp.module.css";
import SwipeableRow from "../AdminPortal/SwipeableRow";
import {
  HubDetailPanel,
  HubFormCard,
  HubListGroup,
  HubNavAdd,
  HubRow,
  HubScreen,
  HubSegment,
  HubSheet,
  HubSheetActions,
  HubToolbar,
  DesktopDialog,
  StatusArchiveTabs,
} from "../AdminPortal/HubPwa";
import s from "../clinic.module.css";
import md from "../common/manageDesktop.module.css";
import {
  Button,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonCard,
  useToast,
} from "../../ui";
import { IconCalendar, IconUsers } from "../../ui/icons";
import GroupsPrerequisiteSetup from "./GroupsPrerequisiteSetup";
import { useActivation, ActivationPageGuide, ActTarget, ActivationSheetHint, useTourFieldValidator } from "../../../activation";
import { useTrainingMode } from "../../../training";

function capacityPct(count, max) {
  if (!max) return null;
  return Math.min(100, Math.round((count / max) * 100));
}

function groupBranchName(group, branches) {
  if (group?.branch?.name) return group.branch.name;
  const id = group?.branch_id || group?.branch?.id;
  if (!id) return null;
  return branches.find((b) => b.id === id)?.name || null;
}

export default function AdminGroups({ staffMode = false }) {
  const tenantId = useSelector((state) => state.auth.tenant?.id);
  const toast = useToast();
  const navigate = useNavigate();
  const {
    refresh: refreshActivation,
    currentStep,
    visible: activationVisible,
    completed: activationCompleted,
    setSubstep,
    currentSubstepId,
    advanceSubstep,
    finishCurrentStep,
  } = useActivation();
  const {
    active: trainingActive,
    currentLesson,
    completed: trainingCompleted,
    trainingSubstepId,
    advanceTrainingSubstep,
    setTrainingSubstep,
  } = useTrainingMode();
  const guideActive =
    (activationVisible && currentStep?.id === "group") ||
    (trainingActive && currentLesson?.id === "group");
  const scheduleGuideActive =
    (activationVisible && currentStep?.id === "schedule") ||
    (trainingActive && currentLesson?.id === "schedule");
  const guideSubstepId = trainingActive ? trainingSubstepId : currentSubstepId;
  const scheduleGuideSubstepId = trainingActive
    ? trainingSubstepId
    : currentSubstepId;
  const groupGuideDone =
    (trainingActive && trainingCompleted.includes("group")) ||
    (activationVisible && activationCompleted.includes("group"));
  const guideSelectTouchRef = useRef({ direction: false, branch: false });
  const groupFormAutoOpenedRef = useRef(false);

  const advanceGuide = useCallback(() => {
    if (trainingActive) advanceTrainingSubstep();
    else advanceSubstep();
  }, [trainingActive, advanceTrainingSubstep, advanceSubstep]);

  const setGuideSubstep = useCallback(
    (subId) => {
      if (trainingActive) setTrainingSubstep("group", subId);
      else setSubstep("group", subId);
    },
    [trainingActive, setTrainingSubstep, setSubstep],
  );

  const setScheduleGuideSubstep = useCallback(
    (subId) => {
      if (trainingActive) setTrainingSubstep("schedule", subId);
      else setSubstep("schedule", subId);
    },
    [trainingActive, setTrainingSubstep, setSubstep],
  );
  const isApp = useAdminHubUi();
  const isMobile = useIsMobile(768);
  const isDesktopManage = !isApp && !isMobile;
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [branches, setBranches] = useState([]);
  const [directions, setDirections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: "",
    direction_id: "",
    branch_id: "",
    max_capacity: "",
  });
  const [members, setMembers] = useState([]);
  const [memberPatientId, setMemberPatientId] = useState("");
  const [editBranchId, setEditBranchId] = useState("");
  const [editName, setEditName] = useState("");
  const [editMaxCapacity, setEditMaxCapacity] = useState("");
  const [editRsvpCapacity, setEditRsvpCapacity] = useState("");
  const [editDirectionId, setEditDirectionId] = useState("");
  const [savingBranch, setSavingBranch] = useState(false);
  const [detailTab, setDetailTab] = useState("members");
  const panelFromUrl = searchParams.get("panel");
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveFutureCount, setArchiveFutureCount] = useState(0);
  const [archiveMode, setArchiveMode] = useState("cancel");
  const [archiveTransferId, setArchiveTransferId] = useState("");
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [transferMember, setTransferMember] = useState(null);
  const [transferTargetId, setTransferTargetId] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [listTab, setListTab] = useState("active");
  const clientsBase = staffMode ? "/staff/app/clients" : "/manage/app/clients";

  useTourFieldValidator(
    "group",
    "group_fill",
    guideActive && guideSubstepId === "group_fill",
    () => {
      if (!form.name.trim()) {
        return { ok: false, message: "Введите название группы" };
      }
      if (!form.direction_id && directions.length > 1) {
        return { ok: false, message: "Выберите направление" };
      }
      if (!form.branch_id && branches.length > 1) {
        return { ok: false, message: "Выберите зал" };
      }
      return true;
    },
  );

  const openGroup = useCallback(
    (groupId) => {
      setSelectedId(groupId);
      if (scheduleGuideActive && scheduleGuideSubstepId === "sched_pick_group") {
        setDetailTab("schedule");
        advanceGuide();
        return;
      }
      if (scheduleGuideActive) {
        setDetailTab("schedule");
        return;
      }
      setDetailTab("members");
    },
    [scheduleGuideActive, scheduleGuideSubstepId, advanceGuide],
  );

  const load = useCallback(async (opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!silent) setLoading(true);
    try {
      const [groupsRes, branchRes, dirRes] = await Promise.all([
        clinicAPI
          .getSportGroupsSummary(
            staffMode ? {} : { include_archived: true },
          )
          .catch(() => null),
        clinicAPI.getBranches().catch(() => null),
        clinicAPI.getSportDirections().catch(() => null),
      ]);
      if (groupsRes) {
        setGroups(groupsRes.groups || []);
      }
      if (branchRes) {
        const branchList = (branchRes.branches || []).filter(
          (b) => b.is_active !== false,
        );
        setBranches(branchList);
        if (branchList.length === 1) {
          setForm((f) => ({ ...f, branch_id: branchList[0].id }));
        }
      }
      if (dirRes) {
        const dirs = (dirRes.directions || []).filter(
          (d) => d.is_active !== false,
        );
        setDirections(dirs);
        if (dirs.length === 1) {
          setForm((f) => ({ ...f, direction_id: dirs[0].id }));
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast, staffMode]);

  const activeGroups = useMemo(
    () => groups.filter((g) => g.is_active !== false),
    [groups],
  );
  const archivedGroups = useMemo(
    () => groups.filter((g) => g.is_active === false),
    [groups],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (panelFromUrl === "schedule") {
      setDetailTab("schedule");
    }
  }, [panelFromUrl]);

  useEffect(() => {
    if (!scheduleGuideActive) return;
    if (selectedId) setDetailTab("schedule");
  }, [scheduleGuideActive, selectedId]);

  useEffect(() => {
    if (!scheduleGuideActive) return;
    if (!selectedId) {
      if (
        scheduleGuideSubstepId !== "sched_pick_group" &&
        scheduleGuideSubstepId !== "sched_fill" &&
        scheduleGuideSubstepId !== "sched_save"
      ) {
        setScheduleGuideSubstep("sched_pick_group");
      }
      return;
    }
    if (detailTab !== "schedule") return;
    const legacy = ["sched_tab", "sched_days", "sched_time", "sched_add"];
    // Уже в карточке группы — сразу «Дни и время», не навигация и не «Сохранить»
    if (
      scheduleGuideSubstepId === "sched_pick_group" ||
      scheduleGuideSubstepId?.endsWith("_nav") ||
      scheduleGuideSubstepId?.endsWith("_nav_hub") ||
      legacy.includes(scheduleGuideSubstepId) ||
      !scheduleGuideSubstepId
    ) {
      setScheduleGuideSubstep("sched_fill");
    }
  }, [
    scheduleGuideActive,
    selectedId,
    detailTab,
    scheduleGuideSubstepId,
    setScheduleGuideSubstep,
  ]);

  const selected = useMemo(
    () => groups.find((g) => g.id === selectedId) || null,
    [groups, selectedId],
  );

  useEffect(() => {
    if (!selected) return;
    setEditName(selected.name || "");
    setEditBranchId(
      selected.branch_id || selected.branch?.id || branches[0]?.id || "",
    );
    setEditMaxCapacity(
      selected.max_capacity != null && selected.max_capacity !== ""
        ? String(selected.max_capacity)
        : "",
    );
    setEditRsvpCapacity(
      selected.rsvp_capacity != null && selected.rsvp_capacity !== ""
        ? String(selected.rsvp_capacity)
        : "",
    );
    setEditDirectionId(
      selected.direction_id || selected.direction?.id || "",
    );
  }, [selected, branches]);

  const loadMembers = useCallback(async (groupId) => {
    if (!groupId) return;
    try {
      const data = await clinicAPI.getGroupMembers(groupId);
      setMembers(data.members || []);
    } catch {
      setMembers([]);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadMembers(selectedId);
    else setMembers([]);
  }, [selectedId, loadMembers]);

  const defaultForm = useCallback(
    () => ({
      name: "",
      direction_id: directions.length === 1 ? directions[0].id : "",
      branch_id: branches.length === 1 ? branches[0].id : branches[0]?.id || "",
      max_capacity: "",
    }),
    [directions, branches],
  );

  useEffect(() => {
    if (!guideActive || groupGuideDone) {
      groupFormAutoOpenedRef.current = false;
      return;
    }
    if (guideSubstepId !== "group_fill") {
      groupFormAutoOpenedRef.current = false;
      return;
    }
    if (showAdd) return;
    if (groupFormAutoOpenedRef.current) return;
    groupFormAutoOpenedRef.current = true;
    setForm(defaultForm());
    setShowAdd(true);
  }, [guideActive, groupGuideDone, guideSubstepId, showAdd, defaultForm]);

  useEffect(() => {
    if (!groupGuideDone || !showAdd) return;
    setShowAdd(false);
  }, [groupGuideDone, showAdd]);

  const openAddForm = () => {
    setForm(defaultForm());
    setShowAdd(true);
    groupFormAutoOpenedRef.current = true;
    if (guideActive && guideSubstepId === "group_new") {
      advanceGuide();
    } else if (guideActive) {
      setGuideSubstep("group_fill");
    }
  };

  const closeAddForm = () => setShowAdd(false);

  const canCreate =
    directions.length > 0 &&
    branches.length > 0 &&
    form.name.trim() &&
    (form.direction_id || directions.length === 1);

  const createGroup = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    try {
      const capacityRaw = String(form.max_capacity || "").trim();
      const capacityNum = capacityRaw ? Number(capacityRaw) : null;
      const r = await clinicAPI.createSpecialization({
        name: form.name.trim(),
        direction_id: form.direction_id || directions[0]?.id,
        branch_id: form.branch_id || branches[0]?.id,
        max_capacity:
          capacityNum != null && Number.isFinite(capacityNum) && capacityNum > 0
            ? Math.round(capacityNum)
            : null,
      });
      trackActivationComplete("group_created", tenantId);
      if (
        guideActive &&
        !groupGuideDone &&
        (guideSubstepId === "group_save" || guideSubstepId === "group_fill")
      ) {
        if (trainingActive) advanceGuide();
        else if (guideSubstepId === "group_fill") finishCurrentStep();
        else advanceGuide();
      }
      setForm(defaultForm());
      setShowAdd(false);
      toast.success("Группа создана");
      refreshActivation?.();
      const newId = r.specialization?.id;
      if (newId) {
        setSelectedId(newId);
        // Сразу вкладка расписания — без обхода «Ещё → Группы → открыть»
        if (guideActive || scheduleGuideActive) {
          setDetailTab("schedule");
          // После завершения урока «группа» ждём кадр и ставим «Дни и время»
          window.requestAnimationFrame(() => {
            setScheduleGuideSubstep("sched_fill");
          });
        }
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const openArchiveFlow = async (g) => {
    if (staffMode) return;
    setArchiveTarget(g);
    setArchiveMode("cancel");
    setArchiveTransferId("");
    setArchiveLoading(true);
    try {
      const preview = await clinicAPI.getSpecializationArchivePreview(g.id);
      setArchiveFutureCount(preview.future_sessions ?? 0);
    } catch {
      setArchiveFutureCount(0);
    } finally {
      setArchiveLoading(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    if (archiveMode === "transfer" && !archiveTransferId) {
      toast.error("Выберите группу для переноса");
      return;
    }
    try {
      const opts = {};
      if (archiveMode === "transfer") {
        opts.transfer_to_group_id = archiveTransferId;
      }
      const r = await clinicAPI.deactivateSpecialization(archiveTarget.id, opts);
      toast.success(r.message || "Группа в архиве");
      if (selectedId === archiveTarget.id) setSelectedId(null);
      setArchiveTarget(null);
      setListTab("archive");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось архивировать");
    }
  };

  const renderArchiveSheet = () => {
    const otherGroups = activeGroups.filter((g) => g.id !== archiveTarget?.id);
    const canTransfer = otherGroups.length > 0;
    return (
    <HubSheet
      open={Boolean(archiveTarget)}
      onClose={() => setArchiveTarget(null)}
      title={archiveTarget ? `Архив: ${archiveTarget.name}` : "Архив группы"}
      subtitle={
        archiveLoading
          ? "Загрузка…"
          : archiveFutureCount > 0
            ? `${archiveFutureCount} будущих занятий`
            : "Будущих занятий нет"
      }
      footer={
        !archiveLoading ? (
          <HubSheetActions
            primary="Архивировать"
            onPrimary={confirmArchive}
            primaryDisabled={archiveMode === "transfer" && !archiveTransferId}
            onSecondary={() => setArchiveTarget(null)}
          />
        ) : null
      }
    >
      {archiveLoading ? (
        <p className={aa.hubSheetHint}>Проверяем расписание…</p>
      ) : (
        <>
          <p className={aa.archiveSheetLead}>
            Группа уйдёт из списка. Будущие занятия этой группы исчезнут из
            расписания сами. Что сделать со спортсменами?
          </p>
          <div className={aa.archiveChoiceList}>
            <button
              type="button"
              className={
                archiveMode === "cancel"
                  ? aa.archiveChoiceOn
                  : aa.archiveChoice
              }
              onClick={() => setArchiveMode("cancel")}
            >
              <strong>Оставить без группы</strong>
              <span>
                Спортсмены станут без группы — потом можно добавить в другую.
                {archiveFutureCount > 0
                  ? ` ${archiveFutureCount} будущих занятий снимутся с расписания.`
                  : ""}
              </span>
            </button>
            {canTransfer ? (
              <button
                type="button"
                className={
                  archiveMode === "transfer"
                    ? aa.archiveChoiceOn
                    : aa.archiveChoice
                }
                onClick={() => setArchiveMode("transfer")}
              >
                <strong>Перенести в другую группу</strong>
                <span>
                  Состав
                  {archiveFutureCount > 0 ? " и будущие занятия" : ""} перейдут
                  в выбранную группу.
                </span>
              </button>
            ) : (
              <p className={aa.hubSheetHint}>
                Других активных групп нет. Если нужно перенести людей —
                сначала создайте новую группу.
              </p>
            )}
          </div>
          {archiveMode === "transfer" && canTransfer ? (
            <Field label="Куда перенести">
              <Select
                value={archiveTransferId}
                onChange={(e) => setArchiveTransferId(e.target.value)}
              >
                <option value="">Выберите группу</option>
                {otherGroups.map((g) => {
                  const count = g.member_count ?? 0;
                  const max = g.max_capacity;
                  const label =
                    max != null && max !== ""
                      ? `${g.name} (${count}/${max})`
                      : `${g.name} (${count})`;
                  const full =
                    max != null &&
                    max !== "" &&
                    Number(max) > 0 &&
                    count >= Number(max);
                  return (
                    <option key={g.id} value={g.id} disabled={full}>
                      {full ? `${label} — заполнена` : label}
                    </option>
                  );
                })}
              </Select>
            </Field>
          ) : null}
        </>
      )}
    </HubSheet>
    );
  };

  const saveBranch = async () => {
    if (!selectedId) return;
    const nextName = String(editName || "").trim();
    if (!nextName) {
      toast.error("Введите название группы");
      return;
    }
    if (!editBranchId) {
      toast.error("Выберите зал");
      return;
    }
    const parseLimit = (raw, label) => {
      const trimmed = String(raw || "").trim();
      if (!trimmed) return { ok: true, value: null };
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 1) {
        return {
          ok: false,
          error: `${label} — целое число от 1, или оставьте пустым`,
        };
      }
      return { ok: true, value: Math.round(n) };
    };
    const roster = parseLimit(editMaxCapacity, "Максимум человек");
    if (!roster.ok) {
      toast.error(roster.error);
      return;
    }
    const rsvp = parseLimit(editRsvpCapacity, "Максимум мест на тренировке");
    if (!rsvp.ok) {
      toast.error(rsvp.error);
      return;
    }
    const currentCapacity =
      selected?.max_capacity != null && selected?.max_capacity !== ""
        ? Number(selected.max_capacity)
        : null;
    const currentRsvp =
      selected?.rsvp_capacity != null && selected?.rsvp_capacity !== ""
        ? Number(selected.rsvp_capacity)
        : null;
    const nameUnchanged = nextName === String(selected?.name || "").trim();
    const branchUnchanged =
      editBranchId === (selected?.branch_id || selected?.branch?.id);
    const capacityUnchanged = currentCapacity === roster.value;
    const rsvpUnchanged = currentRsvp === rsvp.value;
    const currentDirection =
      selected?.direction_id || selected?.direction?.id || "";
    const directionUnchanged = editDirectionId === currentDirection;
    if (
      nameUnchanged &&
      branchUnchanged &&
      capacityUnchanged &&
      rsvpUnchanged &&
      directionUnchanged
    ) {
      return;
    }

    setSavingBranch(true);
    try {
      await clinicAPI.updateSpecialization(selectedId, {
        name: nextName,
        branch_id: editBranchId,
        direction_id: editDirectionId || null,
        max_capacity: roster.value,
        rsvp_capacity: rsvp.value,
      });
      toast.success("Настройки группы сохранены");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSavingBranch(false);
    }
  };

  const restoreGroup = async (g) => {
    if (!g?.id || staffMode) return;
    try {
      await clinicAPI.updateSpecialization(g.id, { is_active: true });
      toast.success("Группа возвращена из архива");
      setListTab("active");
      await load({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось вернуть группу");
    }
  };

  const addMember = async () => {
    if (!selectedId || !memberPatientId) return;
    try {
      await clinicAPI.addGroupMember(selectedId, memberPatientId);
      setMemberPatientId("");
      loadMembers(selectedId);
      load();
      toast.success("Добавлен в группу");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const setTab = (tab) => {
    setDetailTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "schedule") next.set("panel", "schedule");
    else next.delete("panel");
    setSearchParams(next, { replace: true });
  };

  if (loading) {
    return (
      <div className={`${md.desktopHubPage} ${s.groupsHub}`}>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (directions.length === 0 || branches.length === 0) {
    return (
      <div className={`${md.desktopHubPage} ${s.groupsHub}`}>
        <ActivationPageGuide stepIds={["direction", "venue", "group"]} />
        <GroupsPrerequisiteSetup
          directions={directions}
          branches={branches}
          onDirectionsChange={setDirections}
          onBranchesChange={setBranches}
          onReady={async () => {
            await load({ silent: true });
            refreshActivation?.();
          }}
        />
      </div>
    );
  }

  const dirName = (g) => {
    const raw =
      g.direction?.name ||
      g.sport_direction?.name ||
      (typeof g.sport_direction === "string" ? g.sport_direction : null) ||
      directions.find((d) => d.id === g.direction_id)?.name;
    return raw || null;
  };

  const groupCountLabel = `${activeGroups.length} ${
    activeGroups.length === 1
      ? "группа"
      : activeGroups.length < 5
        ? "группы"
        : "групп"
  }`;
  const selectedArchived = selected?.is_active === false;
  const selectedDirectionId =
    selected?.direction_id || selected?.direction?.id || "";
  const settingsUnchanged =
    editName.trim() === String(selected?.name || "").trim() &&
    editBranchId === (selected?.branch_id || selected?.branch?.id) &&
    String(editMaxCapacity || "") ===
      (selected?.max_capacity != null ? String(selected.max_capacity) : "") &&
    String(editRsvpCapacity || "") ===
      (selected?.rsvp_capacity != null ? String(selected.rsvp_capacity) : "") &&
    editDirectionId === selectedDirectionId;

  const renderArchivedAppList = () => (
    <>
      {archivedGroups.length === 0 ? (
        <EmptyState
          title="Архив пуст"
          description="Сюда попадают группы, которые вы убрали из работы. Их можно вернуть."
        />
      ) : (
        <>
          <p className={aa.hubHint}>
            Скрыты из расписания и записи. Можно вернуть или создать новую группу
            с тем же названием.
          </p>
          <HubListGroup>
            {archivedGroups.map((g) => (
              <SwipeableRow
                key={g.id}
                actions={[
                  {
                    id: "restore",
                    label: "Вернуть",
                    tone: "neutral",
                    onClick: () => restoreGroup(g),
                  },
                ]}
              >
                <HubRow
                  title={g.name}
                  meta={[dirName(g), groupBranchName(g, branches) || "Зал не указан"]
                    .filter(Boolean)
                    .join(" · ")}
                  badge="Архив"
                  badgeTone="muted"
                  onClick={() => openGroup(g.id)}
                />
              </SwipeableRow>
            ))}
          </HubListGroup>
        </>
      )}
    </>
  );

  const renderArchivedDesktopList = () =>
    archivedGroups.length === 0 ? (
      <EmptyState
        title="Архив пуст"
        description="Сюда попадают группы, которые вы убрали из работы. Их можно вернуть."
      />
    ) : (
      <>
        <p className={md.desktopMembershipIntro}>
          Скрыты из расписания. Можно вернуть или создать новую группу с тем же
          названием.
        </p>
        <div className={md.desktopHubGrid} aria-label="Архив групп">
          {archivedGroups.map((g) => {
            const venue = groupBranchName(g, branches);
            return (
              <div key={g.id} className={md.desktopHubCardWrap}>
                <button
                  type="button"
                  className={md.desktopHubCard}
                  onClick={() => openGroup(g.id)}
                >
                  <span className={md.desktopHubCardIcon}>📦</span>
                  <span className={md.desktopHubCardBody}>
                    <strong className={md.desktopHubCardName}>{g.name}</strong>
                    <span className={md.desktopHubCardMeta}>
                      {[dirName(g), venue || "Зал не указан"]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <span className={md.desktopHubCardSub}>Архив</span>
                  </span>
                </button>
                <div className={md.desktopHubCardActions}>
                  <button
                    type="button"
                    className={md.desktopHubCardActionBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      restoreGroup(g);
                    }}
                  >
                    Вернуть
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );


  const groupAddFooter = (
    <>
      <ActTarget stepId="group" subId="group_save" as="span" compact>
        {isApp ? (
          <button
            type="submit"
            form="groups-add-form"
            disabled={!canCreate}
            className={aa.hubSheetBtnPrimary}
          >
            Создать
          </button>
        ) : (
          <Button type="submit" form="groups-add-form" disabled={!canCreate}>
            Создать
          </Button>
        )}
      </ActTarget>
      {isApp ? (
        <button
          type="button"
          className={aa.hubSheetBtnGhost}
          onClick={closeAddForm}
        >
          Отмена
        </button>
      ) : (
        <Button type="button" variant="secondary" onClick={closeAddForm}>
          Отмена
        </Button>
      )}
    </>
  );

  const handleGroupNameChange = (value) => {
    setForm((prev) => ({ ...prev, name: value }));
  };

  const getGuideSelectHandlers = () => ({});

  const handleGroupDirectionChange = (value) => {
    setForm((prev) => ({ ...prev, direction_id: value }));
  };

  const handleGroupBranchChange = (value) => {
    setForm((prev) => ({ ...prev, branch_id: value }));
  };

  const renderAddGroupForm = () => (
    <form id="groups-add-form" onSubmit={createGroup}>
      <ActivationSheetHint stepIds={["group"]} />
      <ActTarget stepId="group" subId="group_fill" as="div">
        <Field label="Название">
          <Input
            value={form.name}
            onChange={(e) => handleGroupNameChange(e.target.value)}
            placeholder="Например, Группа 2010"
            required
            autoFocus
          />
        </Field>
        <Field
          label="Направление"
          hint="Вид спорта этой группы — дзюдо, плавание, футбол…"
        >
          <Select
            value={form.direction_id}
            onChange={(e) => handleGroupDirectionChange(e.target.value)}
            {...getGuideSelectHandlers("group_direction", "direction")}
            required
          >
            {directions.length !== 1 ? (
              <option value="">Выберите направление</option>
            ) : null}
            {directions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Зал"
          hint="Где занимается группа по умолчанию"
        >
          <Select
            value={form.branch_id || branches[0]?.id || ""}
            onChange={(e) => handleGroupBranchChange(e.target.value)}
            {...getGuideSelectHandlers("group_branch", "branch")}
            required
          >
            {branches.length !== 1 ? (
              <option value="">Выберите зал</option>
            ) : null}
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Максимум человек"
          hint="Необязательно. Когда мест не останется — в группу нельзя будет добавить новых"
        >
          <Input
            type="number"
            min={1}
            inputMode="numeric"
            value={form.max_capacity}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, max_capacity: e.target.value }))
            }
            placeholder="Например, 12"
          />
        </Field>
      </ActTarget>
    </form>
  );

  const renderAddGroupModal = () => {
    if (isDesktopManage) {
      return (
        <DesktopDialog
          open={showAdd}
          onClose={closeAddForm}
          title="Новая группа"
          subtitle="Название, вид спорта и зал. Расписание — на следующем шаге"
          titleId="groups-add-title"
          footer={groupAddFooter}
        >
          {renderAddGroupForm()}
        </DesktopDialog>
      );
    }

    return (
      <HubSheet
        open={showAdd}
        onClose={closeAddForm}
        title="Новая группа"
        subtitle="Направление, зал и название — расписание настроите позже"
        titleId="groups-add-title"
        footer={groupAddFooter}
      >
        {renderAddGroupForm()}
      </HubSheet>
    );
  };

  const detailTabs = staffMode
    ? [
        { id: "members", label: "Состав" },
        { id: "ranking", label: "Рейтинг" },
        { id: "schedule", label: "Расписание" },
      ]
    : [
        { id: "members", label: "Состав" },
        { id: "ranking", label: "Рейтинг" },
        { id: "schedule", label: "Расписание" },
        { id: "venue", label: "Настройки" },
      ];

  const openGroupCalendar = () => {
    if (!selected) return;
    navigate(
      scheduleUrl({
        groupId: selected.id,
        date: toDateInputValue(),
        view: "month",
      }),
    );
  };

  const openTransfer = (member) => {
    setTransferMember(member);
    setTransferTargetId("");
  };

  const confirmTransfer = async () => {
    if (!transferMember || !transferTargetId || !selected) return;
    setTransferBusy(true);
    try {
      await clinicAPI.transferGroupMember(
        selected.id,
        transferMember.patient_id,
        transferTargetId,
      );
      toast.success("Переведён в другую группу");
      setTransferMember(null);
      loadMembers(selected.id);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setTransferBusy(false);
    }
  };

  const transferTargets = activeGroups.filter((g) => g.id !== selected?.id);

  const renderTransferSheet = () => (
    <HubSheet
      open={Boolean(transferMember)}
      onClose={() => !transferBusy && setTransferMember(null)}
      title="Перевести в другую группу"
      footer={
        transferMember ? (
          <HubSheetActions
            primary={transferBusy ? "…" : "Перевести"}
            primaryDisabled={!transferTargetId || transferBusy}
            onPrimary={confirmTransfer}
            onSecondary={() => !transferBusy && setTransferMember(null)}
          />
        ) : null
      }
    >
      {transferMember ? (
        <>
          <p className={s.clientTabMuted}>
            {transferMember.patient?.full_name || "Клиент"} · из «
            {selected?.name}»
          </p>
          {transferTargets.length === 0 ? (
            <p className={s.clientTabMuted}>
              Нет других активных групп. Создайте группу, чтобы перевести
              спортсмена.
            </p>
          ) : (
            <Field label="Целевая группа">
              <Select
                value={transferTargetId}
                onChange={(e) => setTransferTargetId(e.target.value)}
              >
                <option value="">Выберите группу</option>
                {transferTargets.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </>
      ) : null}
    </HubSheet>
  );

  const renderGroupDetail = () => {
    if (!selected) return null;
    return (
      <>
        <div className={aa.hubInlineActions}>
          <button
            type="button"
            className={aa.hubInlineAction}
            onClick={openGroupCalendar}
          >
            <IconCalendar width={16} height={16} />
            Календарь
          </button>
        </div>

        {detailTab === "venue" ? (
          <HubFormCard title="Настройки группы">
            {selectedArchived ? (
              <p className={aa.hubHint}>
                Группа в архиве. Можно сменить направление и вернуть её.
              </p>
            ) : null}
            <Field label="Название группы">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Например, Дети 7–9"
                maxLength={120}
              />
            </Field>
            <Field label="Направление">
              <Select
                value={editDirectionId}
                onChange={(e) => setEditDirectionId(e.target.value)}
              >
                {directions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Зал для группы">
              <Select
                value={editBranchId}
                onChange={(e) => setEditBranchId(e.target.value)}
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Максимум человек"
              hint="Пусто — без лимита. При заполнении новых участников добавить нельзя"
            >
              <Input
                type="number"
                min={1}
                inputMode="numeric"
                value={editMaxCapacity}
                onChange={(e) => setEditMaxCapacity(e.target.value)}
                placeholder="Например, 12"
              />
            </Field>
            <Field
              label="Максимум мест на тренировке"
              hint="Сколько человек может записаться на одно занятие. Пусто — без лимита (приходит вся группа)"
            >
              <Input
                type="number"
                min={1}
                inputMode="numeric"
                value={editRsvpCapacity}
                onChange={(e) => setEditRsvpCapacity(e.target.value)}
                placeholder="Например, 12"
              />
            </Field>
            <Button
              type="button"
              size="sm"
              disabled={savingBranch || !editName.trim() || settingsUnchanged}
              onClick={saveBranch}
            >
              {savingBranch ? "…" : "Сохранить"}
            </Button>
          </HubFormCard>
        ) : detailTab === "schedule" ? (
            <GroupSchedulePanel
              groupId={selected.id}
              groupName={selected.name}
              directionName={dirName(selected)}
            />
        ) : detailTab === "ranking" ? (
          <GroupLeaderboardPanel groupId={selected.id} />
        ) : (
          <>
            {selectedArchived ? (
              <p className={aa.hubHint}>
                Группа в архиве. Состав сохранён. Верните группу, чтобы снова
                добавлять спортсменов.
              </p>
            ) : (
              <HubFormCard title="Добавить спортсмена">
                <ClientSearchPicker
                  value={memberPatientId}
                  onChange={setMemberPatientId}
                  enrollment={staffMode}
                />
                <Button
                  type="button"
                  disabled={!memberPatientId}
                  onClick={addMember}
                >
                  Добавить
                </Button>
              </HubFormCard>
            )}
            {members.length === 0 ? (
              <p className={s.groupsHubMembersEmpty}>
                Пока никого — найдите клиента выше
              </p>
            ) : (
              <ul className={aa.hubMemberList}>
                {members.map((m) => (
                  <li key={m.id} className={aa.hubMemberRow}>
                    <Link
                      to={`${clientsBase}/${m.patient_id}`}
                      className={aa.hubMemberName}
                    >
                      {m.patient?.full_name || "Клиент"}
                    </Link>
                    <div className={aa.hubMemberActions}>
                      {!staffMode ? (
                        <button
                          type="button"
                          className={aa.hubMemberAction}
                          onClick={() => openTransfer(m)}
                        >
                          Перевести
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={aa.hubMemberAction}
                        onClick={async () => {
                          await clinicAPI.removeGroupMember(
                            selected.id,
                            m.patient_id,
                          );
                          loadMembers(selected.id);
                          load();
                          toast.success("Убран из группы");
                        }}
                      >
                        Убрать
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </>
    );
  };

  if (isApp) {
    if (selectedId && selected) {
      return (
        <>
          <HubScreen
            title={selected.name}
            onBack={() => {
              if (selectedArchived) setListTab("archive");
              setSelectedId(null);
              setDetailTab("members");
            }}
          >
            <p className={aa.hubRowMeta} style={{ margin: "0 0 8px" }}>
              {[dirName(selected), groupBranchName(selected, branches) || "Зал не указан"]
                .filter(Boolean)
                .join(" · ")}
              {selectedArchived ? " · Архив" : ""}
            </p>
            {selectedArchived && !staffMode ? (
              <div className={aa.hubInlineActions}>
                <button
                  type="button"
                  className={aa.hubInlineAction}
                  onClick={() => restoreGroup(selected)}
                >
                  Вернуть из архива
                </button>
              </div>
            ) : null}
            <ActivationPageGuide stepIds={["direction", "venue", "group", "schedule"]} />
            <HubSegment tabs={detailTabs} active={detailTab} onChange={setTab} />
            <HubDetailPanel>{renderGroupDetail()}</HubDetailPanel>
          </HubScreen>
          {!staffMode ? renderAddGroupModal() : null}
          {!staffMode ? renderArchiveSheet() : null}
          {renderTransferSheet()}
        </>
      );
    }

    return (
      <HubScreen
        title={L.groups}
        right={
          staffMode || listTab === "archive" ? null : (
            <ActTarget stepId="group" subId="group_new" as="span" compact>
              <HubNavAdd onClick={openAddForm} ariaLabel="Новая группа" />
            </ActTarget>
          )
        }
      >
        <ActivationPageGuide stepIds={["direction", "venue", "group", "schedule"]} />
        {!staffMode ? (
          <StatusArchiveTabs
            variant="bar"
            value={listTab}
            onChange={setListTab}
            archiveCount={archivedGroups.length}
          />
        ) : null}
        {listTab === "archive" ? (
          renderArchivedAppList()
        ) : (
          <>
            {groupCountLabel ? <HubToolbar countLabel={groupCountLabel} /> : null}
            {activeGroups.length === 0 ? (
              <ActTarget stepId="group" subId="group_new" as="div">
                <EmptyState
                  title="Пока нет групп"
                  description="Создайте первую — детскую, взрослую или соревновательную"
                  actionLabel="Новая группа"
                  onAction={openAddForm}
                />
              </ActTarget>
            ) : (
              <HubListGroup>
                {activeGroups.map((g) => {
                  const count = g.member_count ?? 0;
                  const max = g.max_capacity;
                  const pct = capacityPct(count, max);
                  const venue = groupBranchName(g, branches);
                  return staffMode ? (
                    <HubRow
                      key={g.id}
                      title={g.name}
                      meta={[dirName(g), venue || "Зал не указан"]
                        .filter(Boolean)
                        .join(" · ")}
                      sub={
                        max
                          ? `${count} / ${max} спортсменов`
                          : `${count} спортсменов`
                      }
                      progress={pct}
                      onClick={() => openGroup(g.id)}
                    />
                  ) : (
                    <SwipeableRow
                      key={g.id}
                      actions={[
                        {
                          id: "archive",
                          label: "Архив",
                          tone: "danger",
                          onClick: () => openArchiveFlow(g),
                        },
                      ]}
                    >
                      <ActTarget stepId="schedule" subId="sched_pick_group" as="div">
                        <HubRow
                          title={g.name}
                          meta={[dirName(g), venue || "Зал не указан"]
                            .filter(Boolean)
                            .join(" · ")}
                          sub={
                            max
                              ? `${count} / ${max} спортсменов`
                              : `${count} спортсменов`
                          }
                          progress={pct}
                          onClick={() => openGroup(g.id)}
                        />
                      </ActTarget>
                    </SwipeableRow>
                  );
                })}
              </HubListGroup>
            )}
          </>
        )}
        {!staffMode ? renderAddGroupModal() : null}
        {!staffMode ? renderArchiveSheet() : null}
        {renderTransferSheet()}
      </HubScreen>
    );
  }

  return (
    <div className={`${md.desktopHubPage} ${md.desktopGroupsPage} ${s.groupsHub}`}>
      <ActivationPageGuide stepIds={["direction", "venue", "group", "schedule"]} />
      {selected ? (
        <>
          <button
            type="button"
            className={md.desktopHubBack}
            onClick={() => {
              if (selectedArchived) setListTab("archive");
              setSelectedId(null);
              setDetailTab("members");
            }}
          >
            ← Назад к группам
          </button>
          <div className={md.desktopHubPanel}>
            <header className={md.desktopHubPanelHead}>
              <div>
                <h2 className={md.desktopHubPanelTitle}>{selected.name}</h2>
                <p className={md.desktopHubPanelSub}>
                  {dirName(selected)} ·{" "}
                  {groupBranchName(selected, branches) || "Зал не указан"}
                </p>
              </div>
              <div className={s.groupsHubDetailActions}>
                <Button type="button" size="sm" onClick={openGroupCalendar}>
                  <IconCalendar width={16} height={16} />
                  Календарь
                </Button>
                {!staffMode ? (
                  selectedArchived ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => restoreGroup(selected)}
                    >
                      Вернуть из архива
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => openArchiveFlow(selected)}
                    >
                      В архив
                    </Button>
                  )
                ) : null}
              </div>
            </header>

            <div className={md.desktopGroupTabs} role="tablist">
              {(staffMode
                ? [
                    ["members", "Состав"],
                    ["ranking", "Рейтинг"],
                    ["schedule", "Расписание"],
                  ]
                : [
                    ["members", "Состав"],
                    ["ranking", "Рейтинг"],
                    ["schedule", "Расписание"],
                    ["venue", "Настройки"],
                  ]
              ).map(([tab, label]) => {
                const btn = (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={detailTab === tab}
                    className={
                      detailTab === tab
                        ? md.desktopGroupTabActive
                        : md.desktopGroupTab
                    }
                    onClick={() => setTab(tab)}
                  >
                    {label}
                  </button>
                );
                if (tab === "schedule") {
                  return (
                    <span key={tab}>
                      {btn}
                    </span>
                  );
                }
                return btn;
              })}
            </div>

            <div className={md.desktopGroupTabBody}>
            {detailTab === "venue" ? (
              <div className={md.desktopGroupSettingsCard}>
                <p className={md.desktopGroupSettingsLead}>
                  Название, направление, зал и лимиты. Зал подставляется в расписание.
                </p>
                <div className={md.desktopGroupSettingsRow}>
                  <label className={md.desktopGroupSettingsField}>
                    Название группы
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Например, Дети 7–9"
                      maxLength={120}
                    />
                  </label>
                  <label className={md.desktopGroupSettingsField}>
                    Направление
                    <Select
                      value={editDirectionId}
                      onChange={(e) => setEditDirectionId(e.target.value)}
                    >
                      {directions.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </Select>
                  </label>
                  <label className={md.desktopGroupSettingsField}>
                    Зал для группы
                    <Select
                      value={editBranchId}
                      onChange={(e) => setEditBranchId(e.target.value)}
                    >
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </Select>
                  </label>
                  <label className={md.desktopGroupSettingsField}>
                    Максимум человек
                    <Input
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={editMaxCapacity}
                      onChange={(e) => setEditMaxCapacity(e.target.value)}
                      placeholder="Без лимита"
                    />
                  </label>
                  <label className={md.desktopGroupSettingsField}>
                    Максимум мест на тренировке
                    <Input
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={editRsvpCapacity}
                      onChange={(e) => setEditRsvpCapacity(e.target.value)}
                      placeholder="Без лимита"
                    />
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    disabled={
                      savingBranch || !editName.trim() || settingsUnchanged
                    }
                    onClick={saveBranch}
                  >
                    {savingBranch ? "…" : "Сохранить"}
                  </Button>
                </div>
              </div>
            ) : detailTab === "schedule" ? (
                <GroupSchedulePanel
                  groupId={selected.id}
                  groupName={selected.name}
                  directionName={dirName(selected)}
                />
            ) : detailTab === "ranking" ? (
              <GroupLeaderboardPanel groupId={selected.id} />
            ) : (
              <>
                {selectedArchived ? (
                  <p className={md.desktopMembershipIntro}>
                    Группа в архиве. Состав сохранён. Верните группу, чтобы
                    снова добавлять спортсменов.
                  </p>
                ) : (
                  <div className={md.desktopGroupAddBar}>
                    <ClientSearchPicker
                      value={memberPatientId}
                      onChange={setMemberPatientId}
                      enrollment={staffMode}
                    />
                    <Button
                      type="button"
                      disabled={!memberPatientId}
                      onClick={addMember}
                    >
                      Добавить в группу
                    </Button>
                  </div>
                )}

                <div>
                  <h3 className={md.desktopGroupSectionTitle}>
                    Состав · {members.length}
                  </h3>
                  {members.length === 0 ? (
                    <p className={s.groupsHubMembersEmpty}>
                      Пока никого — найдите клиента выше или добавьте в
                      разделе «Клиенты»
                    </p>
                  ) : (
                    <ul className={md.desktopGroupMembers}>
                      {members.map((m) => (
                        <li key={m.id} className={md.desktopGroupMemberRow}>
                          <Link
                            to={`${clientsBase}/${m.patient_id}`}
                            className={md.desktopGroupMemberName}
                          >
                            {m.patient?.full_name || "Клиент"}
                          </Link>
                          {m.patient?.phone ? (
                            <a
                              href={`tel:${m.patient.phone}`}
                              className={md.desktopGroupMemberPhone}
                            >
                              {m.patient.phone}
                            </a>
                          ) : null}
                          <div className={md.desktopGroupMemberActions}>
                            {!staffMode ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                onClick={() => openTransfer(m)}
                              >
                                Перевести
                              </Button>
                            ) : null}
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={async () => {
                                await clinicAPI.removeGroupMember(
                                  selected.id,
                                  m.patient_id,
                                );
                                loadMembers(selected.id);
                                load();
                                toast.success("Убран из группы");
                              }}
                            >
                              Убрать
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
            </div>
          </div>
        </>
      ) : (
        <>
          <header className={md.desktopHubPageHead}>
            <div className={md.desktopHubPageIntro}>
              <p className={md.desktopHubPageLead}>
                Группа объединяет направление, зал и спортсменов. Зал и
                расписание можно изменить в любой момент.
              </p>
              {listTab === "active" && activeGroups.length > 0 ? (
                <p className={md.desktopHubPageMeta}>{groupCountLabel}</p>
              ) : null}
            </div>
            {!staffMode && listTab === "active" ? (
              <ActTarget stepId="group" subId="group_new" as="span" compact>
                <Button type="button" onClick={openAddForm}>
                  + Новая группа
                </Button>
              </ActTarget>
            ) : null}
          </header>

          {!staffMode ? (
            <StatusArchiveTabs
              value={listTab}
              onChange={setListTab}
              archiveCount={archivedGroups.length}
            />
          ) : null}

          {listTab === "archive" ? (
            renderArchivedDesktopList()
          ) : activeGroups.length === 0 ? (
            <EmptyState
              title="Пока нет групп"
              description="Создайте первую — детскую, взрослую или соревновательную"
              actionLabel="+ Новая группа"
              onAction={openAddForm}
            />
          ) : (
            <div className={md.desktopHubGrid} aria-label="Список групп">
              {activeGroups.map((g) => {
                const count = g.member_count ?? 0;
                const max = g.max_capacity;
                const pct = capacityPct(count, max);
                const venue = groupBranchName(g, branches);
                return (
                  <ActTarget
                    key={g.id}
                    stepId="schedule"
                    subId="sched_pick_group"
                    as="div"
                    className={md.desktopHubCardWrap}
                  >
                    <button
                      type="button"
                      className={md.desktopHubCard}
                      onClick={() => openGroup(g.id)}
                    >
                      <span className={md.desktopHubCardIcon}>🏃</span>
                      <span className={md.desktopHubCardBody}>
                        <strong className={md.desktopHubCardName}>{g.name}</strong>
                        <span className={md.desktopHubCardMeta}>
                          {[dirName(g), venue || "Зал не указан"]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                        <span className={md.desktopHubCardSub}>
                          <IconUsers width={14} height={14} />
                          {count}
                          {max ? ` / ${max}` : ""} спортсменов
                        </span>
                        {pct != null ? (
                          <span className={md.desktopHubCardBar} aria-hidden>
                            <span
                              className={md.desktopHubCardBarFill}
                              style={{ width: `${pct}%` }}
                            />
                          </span>
                        ) : null}
                      </span>
                    </button>
                    {!staffMode ? (
                      <div className={md.desktopHubCardActions}>
                        <button
                          type="button"
                          className={`${md.desktopHubCardActionBtn} ${md.desktopHubCardActionBtnDanger}`}
                          aria-label={`В архив: ${g.name}`}
                          title="В архив"
                          onClick={(e) => {
                            e.stopPropagation();
                            openArchiveFlow(g);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : null}
                  </ActTarget>
                );
              })}
            </div>
          )}
        </>
      )}
      {!staffMode ? renderAddGroupModal() : null}
      {!staffMode ? renderArchiveSheet() : null}
      {renderTransferSheet()}
    </div>
  );
}
